// 預約抽屜（新增／編輯）共用：專業人士可排案時段的即時提示與儲存時提醒。
// 宿主元件需有 data：professional、startDate。載入失敗一律視為「無資料」，不顯示提示也不提醒。
import { getProfessionalAvailability } from '@/api/professionals';
import { getHolidays } from '@/api/holiday';
import {
  buildDayInfo, describeDay, hasWeeklySlots, checkAppointmentAvailability,
} from '@/util/availability';
import msg from '@/util/msg';

// 抽屜內以 professional id 快取 promise，失敗時移除以便重試
function loadAvailabilityCached(cache, id) {
  /* eslint-disable no-param-reassign */
  if (!cache[id]) {
    cache[id] = getProfessionalAvailability(id).catch((e) => {
      delete cache[id];
      throw e;
    });
  }
  /* eslint-enable no-param-reassign */
  return cache[id];
}

// 涵蓋 [startAt, endAt]（unix 秒）的所有年份的假日；任一年失敗則回傳空陣列
async function loadHolidaysBetween(startAt, endAt) {
  const first = new Date(startAt * 1000).getFullYear();
  const last = new Date(Math.max(startAt, endAt) * 1000).getFullYear();
  const years = [];
  for (let y = first; y <= last; y += 1) years.push(y);
  try {
    return (await Promise.all(years.map(getHolidays))).flat();
  } catch (e) {
    return [];
  }
}

export default {
  data() {
    return {
      hintAvailability: null,
      hintHolidays: [],
    };
  },
  computed: {
    availabilityHint() {
      if (!this.hintAvailability || !this.startDate) {
        return '';
      }
      try {
        const date = new Date(this.startDate);
        date.setHours(0, 0, 0, 0);
        const [info] = buildDayInfo([date], this.hintAvailability, this.hintHolidays);
        return describeDay(info, hasWeeklySlots(this.hintAvailability));
      } catch (e) {
        return '';
      }
    },
  },
  watch: {
    professional() {
      this.refreshAvailabilityHint();
    },
    startDate() {
      this.refreshAvailabilityHint();
    },
  },
  created() {
    this.availabilityCache = {};
  },
  methods: {
    async refreshAvailabilityHint() {
      const { professional, startDate } = this;
      this.hintAvailability = null;
      if (professional === undefined || !startDate) {
        return;
      }
      const year = new Date(startDate).getFullYear();
      const [availability, holidays] = await Promise.all([
        loadAvailabilityCached(this.availabilityCache, professional).catch(() => null),
        getHolidays(year).catch(() => []),
      ]);
      // 載入期間選項又變動就丟棄這次結果
      if (professional === this.professional && startDate === this.startDate) {
        this.hintAvailability = availability;
        this.hintHolidays = holidays;
      }
    },
    // action 為 'Create' 或 'Edit'，決定確認按鈕文字。回傳 true 代表可繼續儲存；只有使用者在提醒中選擇取消才回傳 false，其餘任何錯誤都不擋儲存
    async confirmAvailability(appointment, action) {
      if (appointment.professional_id === undefined) {
        return true;
      }
      let reasons;
      try {
        const availability = await loadAvailabilityCached(this.availabilityCache, appointment.professional_id)
          .catch(() => null);
        if (!availability) {
          return true;
        }
        const holidays = await loadHolidaysBetween(appointment.start_at, appointment.end_at);
        reasons = checkAppointmentAvailability({
          availability, holidays, startAt: appointment.start_at, endAt: appointment.end_at,
        });
      } catch (e) {
        return true;
      }
      if (reasons.length === 0) {
        return true;
      }
      try {
        await this.$showConfirm({
          title: msg.availability.reminder.confirmTitle,
          content: `${msg.availability.reminder.confirmIntro}${reasons.join('、')}`,
          confirmType: 'warning',
          confirmButtonText: msg.availability.reminder[`confirmButton${action}`],
          cancelButtonText: msg.availability.reminder.cancelButton,
          customClass: 'button-revert',
        });
        return true;
      } catch (e) {
        return false;
      }
    },
  },
};
